<template>
  <div>
    <template v-if="!isEditing">
      <!-- Compact view - single line -->
      <div class="d-flex align-items-center justify-content-between">
        <div class="flex-grow-1">
          <span class="small" :class="priceData && priceData.price ? 'text-dark' : 'text-muted'">
            {{ displayPrice }}
          </span>
        </div>
        <fa v-if="canEdit" icon="pencil-alt" class="text-muted cursor-pointer ml-2" @click="startEditing" />
      </div>
    </template>
    <template v-else>
      <!-- Edit mode -->
      <div class="d-flex flex-column">
        <div class="mb-2">
          <label class="small">{{ $t('sandoz.price') }}</label>
          <b-form-input
            size="sm"
            type="number"
            step="0.01"
            :value="localPrice"
            @input="updatePrice"
            placeholder="0.00"
          />
        </div>
        <div class="text-center">
          <fa icon="check" class="text-success cursor-pointer" @click="confirmEdit" title="Done" />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'ProductPriceMatrixCell',
  props: {
    productMasterId: {
      type: Number,
      required: true
    },
    salesLineId: {
      type: Number,
      required: true
    },
    priceCategoryId: {
      type: Number,
      required: true
    },
    priceCategoryName: {
      type: String,
      required: true
    },
    priceData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      isEditing: false,
      localPrice: null,
      hasLocalChanges: false
    }
  },
  computed: {
    displayPrice() {
      // If user has made changes, always use localPrice (even if null)
      if (this.hasLocalChanges) {
        return this.formatPrice(this.localPrice)
      }
      // Otherwise, use priceData from prop
      if (this.priceData && this.priceData.price !== null && this.priceData.price !== undefined) {
        return this.formatPrice(this.priceData.price)
      }
      return '-'
    },
    canEdit() {
      // Only allow editing if priceData exists (product has a price record)
      return this.priceData !== null && this.priceData !== undefined
    }
  },
  watch: {
    priceData: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.localPrice = newVal.price
        } else {
          this.localPrice = null
        }
        // Reset change tracking when data changes from external source (e.g., after save/reload)
        this.hasLocalChanges = false
        // Reset edit mode when data changes (e.g., after save/reload)
        this.isEditing = false
      }
    }
  },
  methods: {
    startEditing() {
      // Only allow editing if priceData exists
      if (this.canEdit) {
        this.isEditing = true
      }
    },
    formatPrice(price) {
      if (price === null || price === undefined) {
        return '-'
      }
      return parseFloat(price).toFixed(2)
    },
    updatePrice(value) {
      const priceValue = value === '' || value === null ? null : parseFloat(value)
      this.localPrice = priceValue
      this.hasLocalChanges = true

      // Emit update with all current data
      this.emitUpdate(priceValue)
    },
    confirmEdit() {
      // Ensure the latest value is emitted when confirming
      this.emitUpdate(this.localPrice)
      this.isEditing = false
    },
    emitUpdate(priceValue) {
      const updateData = {
        product_master_id: this.productMasterId,
        price_category_id: this.priceCategoryId,
        sales_line_id: this.salesLineId,
        price: priceValue
      }
      
      // Only include id if it exists (not null/undefined)
      if (this.priceData && this.priceData.id) {
        updateData.id = this.priceData.id
      }

      this.$emit('update', updateData)
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.cursor-pointer:hover {
  opacity: 0.7;
}
</style>
